import './pagination.scss';
import { Icon, Radio, RadioGroup } from '@ampol/design-system';
import { usePagination, useNumericInput } from './hooks';
import { defualtPageLengths } from '../../constants';

/**
 * The 'PageChange' function to be used as an argument
 * @callback PageChange
 * @param {number} pageLength
 * @param {number} pageNumber
 * @return {void}
 *
 * Pagination component
 * @param {Object} props
 * @param {number} props.itemCount The number of items in the dataset
 * @param {number} [props.pageNumber] Current page of results
 * @param {number[]} [props.pageLengths] A list of available page lengths to be provided to the user as options
 * @param {string} [props.name] Added as a suffix to ids to prevent duplicate ids on one page
 * @param {PageChange} [props.onPageChange] Event when page has changed
 */

const Paginator = ({ itemCount, pageNumber = 1, pageLengths = defualtPageLengths, onPageChange, name = '' }) => {
  const { pageLength, pageTotal, increment, decrement, canDecrement, canIncrement, setPageNumber, setPageLength } =
    usePagination({
      currentPage: pageNumber,
      itemCount: itemCount,
      currentPageLength: pageLengths[0],
      onChange: onPageChange,
    });

  const pageNumberInput = useNumericInput({
    defaultValue: pageNumber,
    max: pageTotal,
  });

  const submitPageNumberChange = () => {
    if (!pageNumberInput.isValid) return;
    setPageNumber(Number(pageNumberInput.value));
  };

  const pageSelector = (
    <>
      <button
        className='pagination__button pagination__button--left'
        onClick={decrement}
        disabled={!canDecrement}
        data-testid={'pagination-left'}
      >
        <Icon url='/icons/chevron-down.svg' />
      </button>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitPageNumberChange();
        }}
      >
        <input
          className='pagination__input'
          aria-label='page number'
          value={pageNumberInput.value}
          onChange={pageNumberInput.handleChange}
          onBlur={submitPageNumberChange}
          type='text'
          data-testid={'pagination-input'}
        />
      </form>
      <span>/</span>
      <span data-testid={'pagination-total'}>{pageTotal}</span>
      <button
        className='pagination__button pagination__button--right'
        onClick={increment}
        disabled={!canIncrement}
        data-testid={'pagination-right'}
      >
        <Icon url='/icons/chevron-down.svg' />
      </button>
    </>
  );

  const pageLengthButtonCheckedClass = (value) => (value == pageLength ? 'pagination__pageLengthButton--checked' : '');
  const pageLengthSeletor = pageLengths.length > 1 && (
    <>
      {'showing per page:'}
      {
        <RadioGroup
          color='black'
          name={`${name}-pageLength`}
          onChange={setPageLength}
          direction={'row'}
          data-testid={'pagination-page-size-radio'}
          value={`${pageLength}`}
        >
          {pageLengths.map((availablePageLength, index) => (
            <label
              className={`pagination__pageLengthButton ${pageLengthButtonCheckedClass(availablePageLength)}`}
              key={`radio-${availablePageLength}-${index}`}
            >
              <div className='hidden'>
                <Radio value={`${availablePageLength}`} />
              </div>
              {`${availablePageLength}`}
            </label>
          ))}
        </RadioGroup>
      }
    </>
  );

  return (
    itemCount > 0 && (
      <div className='pagination' data-testid='pagination-container'>
        {pageLengthSeletor}
        {pageSelector}
      </div>
    )
  );
};

export default Paginator;
